import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useChapterAccess, type AccessStatus, type ChapterAccess } from "@/hooks/useChapterAccess";
import { PaywallCard } from "@/components/viewers/ViewerScaffold";

/**
 * Bundles `useChapterAccess` + the appropriate <PaywallCard> for the
 * given mode. Viewer pages use it like:
 *
 *   const { access, paywall } = useViewerAccess(chapterId, "Smart Textbook");
 *   <ViewerScaffold ...>
 *     {paywall ?? (isLocked ? <PaywallCard ... /> : url ? <PDF /> : ...)}
 *   </ViewerScaffold>
 *
 * If `paywall` is non-null the viewer never makes the signed-URL call
 * (we already know access is denied locally). The backend signed-URL
 * endpoint stays the source of truth -- this just saves a round-trip
 * and shows the right CTA at the moment of intent.
 */
export function useViewerAccess(
  chapterId: string | undefined,
  modeLabel: string,
): {
  access: ChapterAccess;
  paywall: ReactNode | null;
  /** True for any "OK to proceed" state (including "unknown" -- backend
   *  signed-URL will decide). Viewers should not call useSignedUrl
   *  until this is true. */
  shouldFetch: boolean;
} {
  const location = useLocation();
  const access = useChapterAccess(chapterId, location.pathname + location.search);
  const paywall = paywallFor(access.status, modeLabel, access.needsClassId);
  const shouldFetch =
    access.status !== "loading" && paywall === null;
  return { access, paywall, shouldFetch };
}

function paywallFor(
  status: AccessStatus,
  modeLabel: string,
  classId: string | null,
): ReactNode | null {
  if (status === "needs_auth") {
    return <PaywallCard modeLabel={modeLabel} reason="needs_auth" classId={classId ?? undefined} />;
  }
  if (status === "needs_subscription") {
    return <PaywallCard modeLabel={modeLabel} reason="needs_subscription" classId={classId ?? undefined} />;
  }
  if (status === "wrong_class") {
    return <PaywallCard modeLabel={modeLabel} reason="wrong_class" classId={classId ?? undefined} />;
  }
  if (status === "needs_onboarding") {
    return <PaywallCard modeLabel={modeLabel} reason="needs_subscription" classId={classId ?? undefined} />;
  }
  return null;
}
